import * as React from 'react';
import {
  Tab, Tabs, Grid, Divider,
} from '@material-ui/core';

import type { Theme } from '@material-ui/core/styles';
import {
  makeStyles, createStyles,
} from '@material-ui/core/styles';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { TabContext, TabPanel } from '@material-ui/lab';
import { differenceInSeconds, intervalToDuration } from 'date-fns';

import globalUseStyles from '../../globalClasses';
import PaperListItem from './PaperListItem';
import JewelerItemCountIcon from './JewelerItemCountIcon';

const useStyles = makeStyles((theme: Theme) => createStyles({
  tab: {
    minWidth: '50%',
  },
  tabPanelOverrides: {
    padding: '0',
  },
  padding: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    marginLeft: theme.spacing(0.25),
    marginRight: theme.spacing(0.25),
  },
}));

interface SidebarRepair {
  barcode: string;
  secondaryText: string;
  status: 'shipLog' | 'scheduled';
  secondaryStatusStart: Date | null;
}

interface JewelerSidebarProps {
  sidebarRepairs: SidebarRepair[];
  secondaryStatusName?: string | null;
  repairItemOnClick: (barcode: string) => void;
  selectedBarcode?: string;
}

const JewelerSidebar: React.FC<JewelerSidebarProps> = ({
  sidebarRepairs, repairItemOnClick, selectedBarcode, secondaryStatusName,
}) => {
  const classes = useStyles();
  const globalStyles = globalUseStyles();
  const [value, setValue] = React.useState('0');

  const [currentTime, setCurrentTime] = React.useState(new Date().getTime());

  function padTo2Digits(num: number) {
    return num.toString().padStart(2, '0');
  }
  const calculatePauseTimer = (lastPausedTime : string) => {
    const startTime = lastPausedTime?.toString() ?? new Date(0);
    const diffInSecs = differenceInSeconds(currentTime, new Date(`${startTime.toString()}z`));

    const duration = intervalToDuration({ start: 0, end: diffInSecs * 1000 });
    const {
      days, hours, minutes, seconds,
    } = duration;

    let updatedHours = hours;
    let formattedDateTime = 'Unable to Calculate Time';
    if (days !== undefined && hours !== undefined && minutes !== undefined && seconds !== undefined && updatedHours !== undefined) {
      if (days > 0) {
        const daysHours = days * 24;
        updatedHours += daysHours;
      }
      formattedDateTime = `${padTo2Digits(updatedHours)}:${padTo2Digits(minutes)}:${padTo2Digits(seconds)}`;
    }
    return formattedDateTime;
  };

  React.useEffect(() => {
    const secTimer = setInterval(() => {
      setCurrentTime(new Date().getTime());
    }, 1000);

    return () => clearInterval(secTimer);
  }, []);

  // eslint-disable-next-line @typescript-eslint/ban-types
  const handleChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setValue(newValue);
  };

  const primaryQueueCount = sidebarRepairs.filter((x) => x.secondaryStatusStart == null).length;
  const secondaryQueueCount = sidebarRepairs.filter((x) => x.secondaryStatusStart != null).length;

  const shipLogItems = (
    sidebarRepairs.filter((x) => x.status === 'shipLog' && x.secondaryStatusStart == null).map((repairItem) => (
      <PaperListItem
        primaryText={repairItem.barcode}
        secondaryText={repairItem.secondaryText}
        onClickFunction={() => repairItemOnClick(repairItem.barcode)}
        iconColor="red"
        icon={<LocalShippingIcon className={globalStyles.smallIconSize} />}
        disabled={selectedBarcode !== undefined && selectedBarcode !== repairItem.barcode}
      />
    )));

  const scheduledItems = (
    sidebarRepairs.filter((x) => x.status === 'scheduled' && x.secondaryStatusStart == null).map((repairItem) => (
      <PaperListItem
        primaryText={repairItem.barcode}
        secondaryText={repairItem.secondaryText}
        onClickFunction={() => repairItemOnClick(repairItem.barcode)}
        iconColor="green"
        icon={<LocalShippingIcon className={globalStyles.smallIconSize} />}
        disabled={selectedBarcode !== undefined && selectedBarcode !== repairItem.barcode}
      />
    )));

  const secondaryStatusShipLogItems = (
    sidebarRepairs.filter((x) => x.status === 'shipLog' && x.secondaryStatusStart != null).map((repairItem) => (
      <PaperListItem
        primaryText={repairItem.barcode}
        secondaryText={repairItem.secondaryText}
        onClickFunction={() => repairItemOnClick(repairItem.barcode)}
        iconColor="red"
        icon={<LocalShippingIcon className={globalStyles.smallIconSize} />}
        disabled={selectedBarcode !== undefined && selectedBarcode !== repairItem.barcode}
        timer={calculatePauseTimer(repairItem?.secondaryStatusStart?.toString() ?? new Date(0).toString())}
      />
    )));

  const secondaryStatusScheduledItems = (
    sidebarRepairs.filter((x) => x.status === 'scheduled' && x.secondaryStatusStart != null).map((repairItem) => (
      <PaperListItem
        primaryText={repairItem.barcode}
        secondaryText={repairItem.secondaryText}
        onClickFunction={() => repairItemOnClick(repairItem.barcode)}
        iconColor="green"
        icon={<LocalShippingIcon className={globalStyles.smallIconSize} />}
        disabled={selectedBarcode !== undefined && selectedBarcode !== repairItem.barcode}
        timer={calculatePauseTimer(repairItem?.secondaryStatusStart?.toString() ?? new Date(0).toString())}
      />
    )));

  return (
    <TabContext value={value}>
      <Tabs
        value={value}
        onChange={handleChange}
        variant="fullWidth"
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab value="0" label={`Queue (${primaryQueueCount})`} className={classes.tab} />
        {secondaryStatusName && (
          <Tab value="1" label={`${secondaryStatusName} (${secondaryQueueCount})`} className={classes.tab} />
        )}
      </Tabs>
      <TabPanel value="0" className={classes.tabPanelOverrides}>
        <Grid container spacing={2} justifyContent="flex-start" alignItems="center">
          <Grid item>
            <JewelerItemCountIcon iconColor="red" count={shipLogItems.length.toString()} icon={<LocalShippingIcon className={globalStyles.smallIconSize} />} />
          </Grid>
          <Grid item>
            <JewelerItemCountIcon iconColor="green" count={scheduledItems.length.toString()} icon={<CalendarTodayIcon className={globalStyles.smallIconSize} />} />
          </Grid>
        </Grid>
        <Divider />
        {shipLogItems}
        {scheduledItems}
      </TabPanel>
      <TabPanel value="1" className={classes.tabPanelOverrides}>
        {secondaryStatusShipLogItems}
        {secondaryStatusScheduledItems}
      </TabPanel>
    </TabContext>
  );
};

export default JewelerSidebar;
